/**
 * `<AssessmentSection>` — Phase 3 item 3.2.
 *
 * Problem List table with ICD-10 codes pulled from `conditions[]` where
 * `category === 'encounter-diagnosis'`. Mirror of the mockup at
 * `ChartPage.tsx:4216-4262`.
 *
 * Edit mode renders an inline `<InlineIcd10Picker>` as the row's first
 * column (shows the code in coral monospace, dropdown of `code  description`
 * pairs filtered against the live ICD-10 ValueSet expansion). A free-text
 * Diagnosis input is column 2 (lets the doctor refine the display
 * independently of the picker's autofill). A Status `<Select>` shows all 6
 * FHIR `Condition.clinicalStatus` values, then a free-text Note input, then
 * a per-row remove (X). `+ Add Diagnosis` appends an empty row whose picker
 * becomes the inline first column.
 *
 * Read mode renders an ICD-10 badge (coral mono), display, status pill, and
 * note text. Visual contract matches the mockup's coral palette (`#e8624a`)
 * — active statuses get the coral pill, every other state falls back to a
 * neutral gray pill.
 */
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Select,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { Fragment } from 'react'
import { IconPlus, IconX } from '@tabler/icons-react'
import { Controller, useFormContext, useWatch, type UseFieldArrayReturn } from 'react-hook-form'
import { Icd10ReplaceChoices } from '@/components/billing/Icd10ReplaceChoices'
import { nonBillableMessage, useIcd10Billability } from '@/components/billing/useIcd10Billability'
import type { ConditionItem, ProgressNoteEditFormValues } from '../progressNoteEditSchema'
import type { NoteDiagnosisChange } from '../billingDxSync'
import { InlineIcd10Picker } from '../InlineIcd10Picker'
import { ICD10_SYSTEM } from '../fhirSystems'
import { MONO_FONT, PROGRESS_NOTE_CORAL as CORAL } from '../tokens'
import { DictateFieldMic } from '../dictation/DictateFieldMic'
import { NoteHeading } from './NoteHeading'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'recurrence', label: 'Recurrence' },
  { value: 'relapse', label: 'Relapse' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'remission', label: 'Remission' },
  { value: 'resolved', label: 'Resolved' },
] as const

const ACTIVE_STATUS = new Set<ConditionItem['clinicalStatus']>(['active', 'recurrence', 'relapse'])

const CORAL_TINT = 'rgba(232,98,74,0.1)'
const NEUTRAL_TINT = '#f3f4f6'
const NEUTRAL_TEXT = '#9ca3af'

export interface AssessmentSectionProps {
  readOnly: boolean
  patientId?: string
  conditionsField: UseFieldArrayReturn<ProgressNoteEditFormValues, 'conditions'>
  /** The visit's date of service; the Problem List flags codes not billable on it (PD-2016). */
  dateOfService?: string
  /** A doctor's edit of an encounter diagnosis, for the note → billing sync (PD-2016). */
  onDiagnosisChange?: (change: NoteDiagnosisChange) => void
}

export function AssessmentSection({
  readOnly,
  patientId,
  conditionsField,
  dateOfService,
  onDiagnosisChange,
}: AssessmentSectionProps) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()
  const conditions = useWatch({ control, name: 'conditions' }) || []
  const familyHistory = useWatch({ control, name: 'familyHistory' }) || []
  const assessmentNarrative = useWatch({ control, name: 'summary.assessment' }) ?? ''

  // Hide an empty impression in read mode (faithfulness); always editable.
  const showClinicalImpression = !readOnly || assessmentNarrative.trim() !== ''

  // Encounter diagnoses (vs problem-list-item) drive the Problem List view in
  // the mockup. Past medical history (problem-list-item) flows through the
  // adapter into SoapNoteDto.pastMedicalHistory.
  const dxIndices = conditions
    .map((c, idx) => ({ c, idx }))
    .filter(({ c }) => c.category === 'encounter-diagnosis')

  const dxCodes = [
    ...new Set(
      conditions.filter((c) => c.category === 'encounter-diagnosis' && c.code).map((c) => c.code),
    ),
  ].sort()
  const { query: billability, byCode: billabilityByCode } = useIcd10Billability(
    dxCodes,
    dateOfService ?? '',
    !readOnly && Boolean(dateOfService),
  )

  // A flagged diagnosis gets a follow-on row for its badge and replacements, which would shift
  // Mantine's nth-of-type stripes, so each row carries its own: diagnosis i is shaded when i is
  // odd (what `striped="even"` gave), its follow-on row shares that shade, and `data-striped`
  // names the parity of the row's rendered position.
  const stripeAt = (position: number) => (position % 2 === 1 ? 'odd' : 'even')
  const dxRows: Array<
    (typeof dxIndices)[number] & {
      check: ReturnType<typeof billabilityByCode.get>
      rowStripe?: 'odd' | 'even'
      replaceStripe?: 'odd' | 'even'
    }
  > = []
  let renderedRows = 0
  for (const [i, row] of dxIndices.entries()) {
    const check =
      !readOnly && row.c.code && billability.data?.checked !== false
        ? billabilityByCode.get(row.c.code)
        : undefined
    const shaded = i % 2 === 1
    const position = renderedRows + 1
    renderedRows += check?.billable === false ? 2 : 1
    dxRows.push({
      ...row,
      check,
      rowStripe: shaded ? stripeAt(position) : undefined,
      replaceStripe: shaded ? stripeAt(position + 1) : undefined,
    })
  }

  // A code another encounter-diagnosis row still holds stays on billing.
  const codeOnOtherRow = (idx: number, code: string) =>
    conditions.some(
      (other, j) => j !== idx && other.category === 'encounter-diagnosis' && other.code === code,
    )
  const reportCodeChange = (idx: number, from: string, to: { code: string; display: string }) => {
    if (!onDiagnosisChange || from === to.code) return
    // A cleared row leaves billing the way a deleted row does.
    if (!to.code) {
      if (!codeOnOtherRow(idx, from)) onDiagnosisChange({ kind: 'remove', code: from })
      return
    }
    const dx = { system: ICD10_SYSTEM, code: to.code, display: to.display }
    onDiagnosisChange(
      from && !codeOnOtherRow(idx, from) ? { kind: 'rename', from, to: dx } : { kind: 'add', dx },
    )
  }

  // Chronic problems (problem-list-item) are excluded from the encounter Problem
  // List above; surface them below it — editable in edit mode with the same row
  // UI as the Problem List, read-only when locked so the note matches the chart
  // Diagnosis tab + patient portal (PD-1177). The adapter routes these rows into
  // SoapNoteDto.pastMedicalHistory.
  const pmhIndices = conditions
    .map((c, idx) => ({ c, idx }))
    .filter(({ c }) => c.category === 'problem-list-item')

  return (
    <Box mb="xl">
      <NoteHeading level="section" id="assessment" label="Assessment" />

      {showClinicalImpression && (
        <Box mb="lg">
          <NoteHeading level="sub" id="clinical-impression" label="Clinical Impression" />
          {readOnly ? (
            <Text size="sm" c="dark.7" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {assessmentNarrative}
            </Text>
          ) : (
            <Controller
              control={control}
              name="summary.assessment"
              render={({ field }) => (
                <Textarea
                  autosize
                  minRows={3}
                  placeholder="Clinical impression / medical decision-making"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.currentTarget.value)}
                  onBlur={field.onBlur}
                  data-testid="assessment-clinical-impression"
                  rightSectionPointerEvents="all"
                  rightSection={
                    <DictateFieldMic
                      field={{ path: 'summary.assessment', label: 'Assessment' }}
                      testId="assessment-clinical-impression"
                    />
                  }
                />
              )}
            />
          )}
        </Box>
      )}

      <Box>
        <Group justify="space-between" align="baseline" mb="xs">
          <NoteHeading level="sub" id="problem-list" label="Problem List" mb={0}>
            <Text component="span" c="dimmed" size="xs">
              LOINC 11450-4
            </Text>
          </NoteHeading>
        </Group>

        {dxIndices.length === 0 && readOnly ? (
          <Text size="xs" c="dimmed">
            No diagnoses captured.
          </Text>
        ) : (
          <Table withTableBorder striped={false} verticalSpacing="xs" highlightOnHover>
            <Table.Thead style={{ background: '#f9fafb' }}>
              <Table.Tr>
                <Table.Th
                  style={{ width: readOnly ? 110 : 220, color: '#374151', fontWeight: 700 }}
                >
                  ICD-10
                </Table.Th>
                <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Diagnosis</Table.Th>
                <Table.Th style={{ width: 150, color: '#374151', fontWeight: 700 }}>
                  Status
                </Table.Th>
                {!readOnly && <Table.Th style={{ width: 40 }} aria-label="actions" />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dxRows.map(({ c, idx, check, rowStripe, replaceStripe }) => {
                const isActive = ACTIVE_STATUS.has(c.clinicalStatus)
                const notBillable = check?.billable === false
                const badge = notBillable && (
                  <Tooltip label={nonBillableMessage([c.code])} multiline w={280}>
                    <Badge
                      color="red"
                      variant="light"
                      size="xs"
                      radius="sm"
                      style={{ textTransform: 'none', flexShrink: 0 }}
                      data-testid={`assessment-not-billable-${idx}`}
                    >
                      Not billable
                    </Badge>
                  </Tooltip>
                )
                return (
                  <Fragment key={c.id}>
                    <Table.Tr
                      data-testid={`assessment-row-${c.code || c.id}`}
                      data-striped={rowStripe}
                      style={notBillable ? { borderBottom: 'none' } : undefined}
                    >
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        {readOnly ? (
                          <Text
                            size="sm"
                            fw={700}
                            style={{
                              fontFamily: MONO_FONT,
                              color: isActive ? CORAL : NEUTRAL_TEXT,
                            }}
                          >
                            {c.code || '—'}
                          </Text>
                        ) : (
                          <InlineIcd10Picker
                            value={
                              c.code
                                ? { system: ICD10_SYSTEM, code: c.code, display: c.display }
                                : undefined
                            }
                            patientId={patientId}
                            onChange={(picked) => {
                              setValue(`conditions.${idx}.code`, picked.code, {
                                shouldDirty: true,
                              })
                              // A different code brings its own description; re-picking
                              // the same code keeps what the doctor typed (PD-2016).
                              const existingDisplay = c.display?.trim() ?? ''
                              if (
                                picked.code !== c.code ||
                                !existingDisplay ||
                                existingDisplay === c.code
                              ) {
                                setValue(`conditions.${idx}.display`, picked.display, {
                                  shouldDirty: true,
                                })
                              }
                              reportCodeChange(idx, c.code, picked)
                            }}
                            testId={`assessment-icd10-${idx}`}
                          />
                        )}
                      </Table.Td>
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        {readOnly ? (
                          <Text size="sm" fw={500} c="dark.9">
                            {c.display || '—'}
                          </Text>
                        ) : (
                          <TextInput
                            size="xs"
                            value={c.display}
                            onChange={(e) =>
                              setValue(`conditions.${idx}.display`, e.currentTarget.value, {
                                shouldDirty: true,
                              })
                            }
                            placeholder="Diagnosis description"
                          />
                        )}
                      </Table.Td>
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        {readOnly ? (
                          <Badge
                            radius="xl"
                            size="sm"
                            variant="light"
                            styles={{
                              root: {
                                background: isActive ? CORAL_TINT : NEUTRAL_TINT,
                                color: isActive ? CORAL : NEUTRAL_TEXT,
                                fontWeight: 700,
                                textTransform: 'none',
                              },
                            }}
                          >
                            {STATUS_OPTIONS.find((o) => o.value === c.clinicalStatus)?.label ||
                              c.clinicalStatus}
                          </Badge>
                        ) : (
                          <Select
                            size="xs"
                            data={STATUS_OPTIONS as unknown as { value: string; label: string }[]}
                            value={c.clinicalStatus}
                            onChange={(value) => {
                              if (!value) return
                              setValue(
                                `conditions.${idx}.clinicalStatus`,
                                value as ConditionItem['clinicalStatus'],
                                { shouldDirty: true },
                              )
                            }}
                          />
                        )}
                      </Table.Td>
                      {!readOnly && (
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => {
                              conditionsField.remove(idx)
                              if (c.code && !codeOnOtherRow(idx, c.code)) {
                                onDiagnosisChange?.({ kind: 'remove', code: c.code })
                              }
                            }}
                            aria-label="Remove diagnosis"
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Table.Td>
                      )}
                    </Table.Tr>
                    {notBillable && (
                      <Table.Tr
                        data-testid={`assessment-replace-row-${idx}`}
                        data-striped={replaceStripe}
                      >
                        <Table.Td colSpan={4} style={{ paddingTop: 0 }}>
                          {/* A pick replaces this row in place, so the diagnosis keeps its
                            position in the note and on billing. */}
                          {check.children.length > 0 ? (
                            <Icd10ReplaceChoices
                              // Keeps the chips out of the table's auto-layout min width, so
                              // they wrap inside the cell instead of widening the table.
                              style={{ contain: 'inline-size' }}
                              wrapLabels
                              leading={badge}
                              choices={check.children}
                              onPick={(child) => {
                                setValue(`conditions.${idx}.code`, child.code, {
                                  shouldDirty: true,
                                })
                                setValue(`conditions.${idx}.display`, child.display, {
                                  shouldDirty: true,
                                })
                                reportCodeChange(idx, c.code, child)
                              }}
                            />
                          ) : (
                            badge
                          )}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                )
              })}
            </Table.Tbody>
          </Table>
        )}

        {!readOnly && (
          <Button
            size="xs"
            variant="light"
            color="blue"
            mt="sm"
            leftSection={<IconPlus size={14} />}
            onClick={() =>
              conditionsField.append({
                id: crypto.randomUUID(),
                code: '',
                display: '',
                category: 'encounter-diagnosis',
                clinicalStatus: 'active',
                onsetDateTime: null,
                severity: null,
                rank: null,
                note: '',
              })
            }
            data-testid="assessment-add-diagnosis"
          >
            Add Diagnosis
          </Button>
        )}
      </Box>

      {(!readOnly || pmhIndices.length > 0) && (
        <Box mt="lg">
          <NoteHeading level="sub" id="past-medical-history" label="Past Medical History" />
          <Table withTableBorder striped="even" verticalSpacing="xs" highlightOnHover>
            <Table.Thead style={{ background: '#f9fafb' }}>
              <Table.Tr>
                <Table.Th
                  style={{ width: readOnly ? 110 : 220, color: '#374151', fontWeight: 700 }}
                >
                  ICD-10
                </Table.Th>
                <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Diagnosis</Table.Th>
                <Table.Th style={{ width: 150, color: '#374151', fontWeight: 700 }}>
                  Status
                </Table.Th>
                {!readOnly && <Table.Th style={{ width: 40 }} aria-label="actions" />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pmhIndices.map(({ c, idx }) => {
                const isActive = ACTIVE_STATUS.has(c.clinicalStatus)
                return (
                  <Table.Tr key={c.id} data-testid={`pmh-row-${c.code || c.id}`}>
                    <Table.Td style={{ verticalAlign: 'top' }}>
                      {readOnly ? (
                        <Text
                          size="sm"
                          fw={700}
                          style={{ fontFamily: MONO_FONT, color: isActive ? CORAL : NEUTRAL_TEXT }}
                        >
                          {c.code || '—'}
                        </Text>
                      ) : (
                        <InlineIcd10Picker
                          value={
                            c.code
                              ? { system: ICD10_SYSTEM, code: c.code, display: c.display }
                              : undefined
                          }
                          patientId={patientId}
                          onChange={(picked) => {
                            setValue(`conditions.${idx}.code`, picked.code, {
                              shouldDirty: true,
                            })
                            // A different code brings its own description; re-picking
                            // the same code keeps what the doctor typed (PD-2016).
                            const existingDisplay = c.display?.trim() ?? ''
                            if (
                              picked.code !== c.code ||
                              !existingDisplay ||
                              existingDisplay === c.code
                            ) {
                              setValue(`conditions.${idx}.display`, picked.display, {
                                shouldDirty: true,
                              })
                            }
                          }}
                          testId={`pmh-icd10-${idx}`}
                        />
                      )}
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'top' }}>
                      {readOnly ? (
                        <Group gap={8} wrap="nowrap">
                          <Text size="sm" fw={500} c="dark.9">
                            {c.display || '—'}
                          </Text>
                          {c.external && (
                            <Badge size="xs" color="gray" variant="light">
                              Chart
                            </Badge>
                          )}
                        </Group>
                      ) : (
                        <Group gap={8} wrap="nowrap">
                          <TextInput
                            size="xs"
                            style={{ flex: 1 }}
                            value={c.display}
                            onChange={(e) =>
                              setValue(`conditions.${idx}.display`, e.currentTarget.value, {
                                shouldDirty: true,
                              })
                            }
                            placeholder="Diagnosis description"
                          />
                          {c.external && (
                            <Badge size="xs" color="gray" variant="light">
                              Chart
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'top' }}>
                      {readOnly ? (
                        <Badge
                          radius="xl"
                          size="sm"
                          variant="light"
                          styles={{
                            root: {
                              background: isActive ? CORAL_TINT : NEUTRAL_TINT,
                              color: isActive ? CORAL : NEUTRAL_TEXT,
                              fontWeight: 700,
                              textTransform: 'none',
                            },
                          }}
                        >
                          {STATUS_OPTIONS.find((o) => o.value === c.clinicalStatus)?.label ||
                            c.clinicalStatus}
                        </Badge>
                      ) : (
                        <Select
                          size="xs"
                          data={STATUS_OPTIONS as unknown as { value: string; label: string }[]}
                          value={c.clinicalStatus}
                          onChange={(value) => {
                            if (!value) return
                            setValue(
                              `conditions.${idx}.clinicalStatus`,
                              value as ConditionItem['clinicalStatus'],
                              { shouldDirty: true },
                            )
                          }}
                        />
                      )}
                    </Table.Td>
                    {!readOnly && (
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        {c.external ? (
                          <Tooltip
                            label="This will not remove the condition from the patient chart."
                            multiline
                            w={220}
                            withArrow
                          >
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => conditionsField.remove(idx)}
                              aria-label="Remove from note"
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => conditionsField.remove(idx)}
                            aria-label="Remove condition"
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        )}
                      </Table.Td>
                    )}
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>

          {!readOnly && (
            <Button
              size="xs"
              variant="light"
              color="blue"
              mt="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                conditionsField.append({
                  id: crypto.randomUUID(),
                  code: '',
                  display: '',
                  category: 'problem-list-item',
                  clinicalStatus: 'active',
                  onsetDateTime: null,
                  severity: null,
                  rank: null,
                  note: '',
                })
              }
              data-testid="assessment-add-pmh"
            >
              Add Condition
            </Button>
          )}
        </Box>
      )}

      {/* Chart-derived Family History (PD-1377): read-only rows, hidden in BOTH
          modes when empty — no manual entry, the chart stays the source. */}
      {familyHistory.length > 0 && (
        <Box mt="lg">
          <NoteHeading level="sub" id="family-history" label="Family History" />
          <Table withTableBorder striped="even" verticalSpacing="xs" highlightOnHover>
            <Table.Thead style={{ background: '#f9fafb' }}>
              <Table.Tr>
                <Table.Th style={{ width: 220, color: '#374151', fontWeight: 700 }}>
                  Relative
                </Table.Th>
                <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Conditions</Table.Th>
                {!readOnly && <Table.Th style={{ width: 40 }} aria-label="actions" />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {familyHistory.map((row) => (
                <Table.Tr key={row.id} data-testid={`family-history-row-${row.id}`}>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Group gap={8} wrap="nowrap">
                      <Text size="sm" fw={500} c="dark.9">
                        {row.relative}
                        {row.deceased ? ' (deceased)' : ''}
                      </Text>
                      {/* Long relative names wrap in the fixed column — the badge must not squeeze. */}
                      <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
                        Chart
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Text size="sm" c="dark.7">
                      {row.conditions.join(', ')}
                    </Text>
                  </Table.Td>
                  {!readOnly && (
                    <Table.Td style={{ verticalAlign: 'top' }}>
                      <Tooltip
                        label="This will not remove the history from the patient chart."
                        multiline
                        w={220}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() =>
                            setValue(
                              'familyHistory',
                              familyHistory.filter((r) => r.id !== row.id),
                              { shouldDirty: true },
                            )
                          }
                          aria-label="Remove from note"
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Box>
  )
}
